import type { TaskBlock, WorkflowTemplate } from '@pm/schemas';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  SkeletonRows,
  Tabs,
  Textarea,
} from '@pm/ui';
import { Boxes, Plus, Workflow } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate } from '@/lib/format';
import { useInvalidating, useTaskBlocks, useWorkflowTemplates, wfKeys } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { BlockEditor } from './BlockEditor';

/** SPEC §22-§24 — lista de templates de workflow e blocos reutilizáveis. */
export function WorkflowsPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'templates' | 'blocks'>('templates');
  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        title="Workflows"
        subtitle="Templates versionados de etapas e tarefas. Publicar congela uma versão; instâncias antigas não mudam."
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'templates', label: 'Templates' },
          { value: 'blocks', label: 'Blocos' },
        ]}
      />
      {tab === 'templates' ? (
        <TemplatesSection canManage={can('workflows.manage')} />
      ) : (
        <BlocksSection canManage={can('workflows.manage')} />
      )}
    </div>
  );
}

function TemplatesSection({ canManage }: { canManage: boolean }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { data = [], isLoading, isError, error, refetch } = useWorkflowTemplates();
  const [create, setCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const save = useInvalidating(
    (input: { name: string; description?: string }) =>
      api.post<WorkflowTemplate>('/workflow-templates', input),
    [wfKeys.templates],
  );

  return (
    <div className="flex flex-col gap-gap">
      {canManage ? (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setCreate(true)}>
            <Plus /> Novo workflow
          </Button>
        </div>
      ) : null}
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Workflow}
            title="Nenhum workflow"
            description="Crie um template, defina etapas e tarefas, e publique para começar a instanciar."
            action={
              canManage ? (
                <Button variant="primary" size="sm" onClick={() => setCreate(true)}>
                  <Plus /> Novo workflow
                </Button>
              ) : undefined
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Workflow</th>
                <th className="h-8 px-3 text-left font-medium">Publicado</th>
                <th className="h-8 px-3 text-left font-medium">Rascunho</th>
                <th className="h-8 px-3 text-right font-medium">Instâncias</th>
                <th className="h-8 px-3 text-left font-medium">Criado</th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <tr
                  key={t.id}
                  className="h-row cursor-pointer border-b border-border last:border-b-0 hover:bg-surface-2"
                  onClick={() => navigate(`/workflows/${t.id}`)}
                >
                  <td className="px-3">
                    <Link to={`/workflows/${t.id}`} className="font-medium hover:text-accent">
                      {t.name}
                    </Link>
                    {t.status === 'ARCHIVED' ? (
                      <Badge tone="neutral" className="ml-2">
                        Arquivado
                      </Badge>
                    ) : null}
                    {t.description ? (
                      <span className="ml-2 text-sm text-text-3">{t.description}</span>
                    ) : null}
                  </td>
                  <td className="px-3">
                    {t.published ? (
                      <span className="inline-flex items-center gap-2">
                        <Badge tone="success">v{t.published.version}</Badge>
                        <span className="font-mono text-xs text-text-3">
                          {t.published.stages_count} etapas
                        </span>
                      </span>
                    ) : (
                      <Badge tone="neutral">Não publicado</Badge>
                    )}
                  </td>
                  <td className="px-3 font-mono text-xs text-text-3">
                    {t.draft ? `${t.draft.stages_count} etapas` : '—'}
                  </td>
                  <td className="px-3 text-right font-mono text-sm">
                    {t.versions.reduce((n, v) => n + v.instances_count, 0)}
                  </td>
                  <td className="px-3 font-mono text-xs text-text-3">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent title="Novo workflow" size="sm">
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(
                { name, description: description || undefined },
                {
                  onSuccess: (t) => (
                    toast.success('Workflow criado'),
                    setCreate(false),
                    navigate(`/workflows/${t.id}`)
                  ),
                  onError: (err) => toast.error(err),
                },
              );
            }}
          >
            <Field label="Nome" htmlFor="wf-name">
              <Input
                id="wf-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Onboarding de cliente"
              />
            </Field>
            <Field label="Descrição" htmlFor="wf-desc">
              <Textarea
                id="wf-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[72px]"
              />
            </Field>
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={() => setCreate(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={save.isPending}
                disabled={name.trim().length < 2}
              >
                Criar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BlocksSection({ canManage }: { canManage: boolean }) {
  const toast = useToast();
  const { data = [], isLoading } = useTaskBlocks();
  const [name, setName] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const create = useInvalidating(
    (n: string) => api.post<TaskBlock>('/task-blocks', { name: n }),
    [wfKeys.blocks],
  );
  return (
    <div className="grid gap-gap-lg lg:grid-cols-[1fr_320px]">
      <div className="flex flex-col gap-gap">
        {isLoading ? (
          <div className="rounded-md border border-border bg-surface-1">
            <SkeletonRows />
          </div>
        ) : data.length === 0 ? (
          <div className="rounded-md border border-border bg-surface-1">
            <EmptyState
              icon={Boxes}
              title="Nenhum bloco"
              description="Blocos agrupam tarefas reutilizáveis (Acessos, Meta Ads, Kick-off) para anexar em etapas."
            />
          </div>
        ) : (
          data.map((b) => (
            <section key={b.id} className="rounded-md border border-border bg-surface-1 p-4">
              <header className="mb-2 flex items-center gap-2">
                <h2 className="text-lg font-medium">{b.name}</h2>
                <span className="font-mono text-xs text-text-3">{b.templates.length} tarefas</span>
                {canManage ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setOpen(open === b.id ? null : b.id)}
                  >
                    {open === b.id ? 'Fechar' : 'Editar'}
                  </Button>
                ) : null}
              </header>
              {open === b.id ? (
                <BlockEditor block={b} />
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {b.templates.map((t) => (
                    <li
                      key={t.id}
                      className="rounded-sm border border-border bg-surface-2 px-2 py-0.5 text-sm"
                    >
                      {t.title}
                    </li>
                  ))}
                  {b.templates.length === 0 ? (
                    <li className="text-sm text-text-3">Sem tarefas.</li>
                  ) : null}
                </ul>
              )}
            </section>
          ))
        )}
      </div>
      {canManage ? (
        <form
          className="flex flex-col gap-3 self-start rounded-md border border-border bg-surface-1 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(name.trim(), {
              onSuccess: () => (setName(''), toast.success('Bloco criado')),
              onError: (err) => toast.error(err),
            });
          }}
        >
          <span className="label-caps">Novo bloco</span>
          <Input
            placeholder="Nome do bloco"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={create.isPending}
            disabled={name.trim().length < 2}
          >
            <Plus /> Criar bloco
          </Button>
        </form>
      ) : null}
    </div>
  );
}
